import React, {
  FC,
  SyntheticEvent,
  memo,
  useCallback,
  useMemo,
  useState,
} from 'react';

import Decimal from 'decimal.js';

import Typography from '@mui/material/Typography';

import {
  useSimulateUpdatePriceRanges,
  useUpdatePriceRange,
} from '@services/contract/vault/farmConnector.contract';
import { useWeightAmountsLiquidity } from '@services/contract/vault/liquidityHelperV3.contract';

import { useAuth } from '@contexts/common/AuthorizationContext';
import { modalActions } from '@contexts/common/ModalContext';
import { vaultActions } from '@contexts/vaults/VaultContext';

import useDebounce from '@hooks/common/useDebounce';
import { useMediaQuery } from '@hooks/common/useMediaQuery';
import { useUpdateConfigLmAction } from '@hooks/vaults/useUpdateConfigLmAction';

import {
  determineInitialMode,
  generateQuickButtonsConfig,
  getPriceImpactLevel,
  getPriorityFarmTag,
  getV3FarmTags,
  inversePercent,
  isConnectorRangeWidthType,
} from '@helpers/vaults/vault.helpers';

import { toBN } from '@utils/formaters.utils';
import { isAddressesEqual } from '@utils/tokens.utils';

import Button from '@components/common/Button';
import Loader from '@components/common/Loader';
import Switch from '@components/common/Switch';
import SwitchChainButton from '@components/common/SwitchChainButton';
import Tabs from '@components/common/Tabs';
import FarmButton from '@components/vaults/FarmButton';
import FarmDetails from '@components/vaults/FarmDetails';
import AutomaticRebalancing from '@components/vaults/ManageLiquidity/AutomaticRebalancing';
import Chart from '@components/vaults/ManageLiquidity/Chart';
import CounterBox from '@components/vaults/ManageLiquidity/CounterBox';
import EstimatedApy from '@components/vaults/ManageLiquidity/EstimatedApy';
import FarmV3Tag from '@components/vaults/ManageLiquidity/FarmV3Tag';
import ProjectedLiquidityPosition from '@components/vaults/ManageLiquidity/LiquidityPosition';
import SelectRange from '@components/vaults/ManageLiquidity/SelectRange';
import ManageLiquidityCounter from '@components/vaults/ManageLiquidityCounter';
import TokenWeight from '@components/vaults/TokenWeight';

import { MODAL_TYPES } from '@constants/common/modals.constants';
import { Size, Variant } from '@constants/common/ui.constants';
import {
  ConnectorRangeWidthType,
  FarmTag,
  PERCENTAGE_LIMIT_RANGE,
  PriceImpactLevel,
  QUICK_CONNECTOR_TYPE,
  RangeTab,
} from '@constants/vaults/vault.constants';

import { VaultDto } from '@dto/vaults/vaults.dto';

import { VaultFarmsType } from '@app-types/vaults/vault.types';
import {
  VaultFarmsV3ItemType,
  VaultInfoType,
} from '@app-types/vaults/vaultContext.types';

import useStyles from './styles.module';

interface ManageLiquidityProps {
  vault: VaultDto;
  vaultInfo: VaultInfoType;
  onCancel: () => void;
  initialMode: ConnectorRangeWidthType | null;
  selectedV3Farm: VaultFarmsType;
  farmPriorityTag: FarmTag;
  currentV3PoolInfo: VaultFarmsV3ItemType;
  setV3Farm: React.Dispatch<React.SetStateAction<VaultFarmsType>>;
}

const ManageLiquidity: FC<ManageLiquidityProps> = ({
  vaultInfo,
  vault,
  onCancel,
  initialMode,
  selectedV3Farm,
  currentV3PoolInfo,
  farmPriorityTag,
  setV3Farm,
}) => {
  const { classes } = useStyles();
  const { isMobile } = useMediaQuery();
  const { account } = useAuth();
  const { vaultV3Farms, vaultFarmsV3Info, vaultFarms } = vaultInfo;

  const [tokenTab, setTokenTab] = useState<string>(
    currentV3PoolInfo.token0.address,
  );

  const [percentage, setPercentage] = useState({
    min: toBN(currentV3PoolInfo.initialMinPercent),
    max: toBN(currentV3PoolInfo.initialMaxPercent),
  });

  const [quickPresetType, setQuickPresetType] =
    useState<ConnectorRangeWidthType | null>(initialMode);

  const isInverseMode = useMemo(
    () => isAddressesEqual(tokenTab, currentV3PoolInfo.token1.address),
    [currentV3PoolInfo, tokenTab],
  );

  const tokens = useMemo(
    () => ({
      token0: isInverseMode
        ? currentV3PoolInfo?.token1
        : currentV3PoolInfo?.token0,
      token1: isInverseMode
        ? currentV3PoolInfo?.token0
        : currentV3PoolInfo?.token1,
    }),
    [isInverseMode, currentV3PoolInfo],
  );

  const farmTags = useMemo(
    () => getV3FarmTags(selectedV3Farm),
    [selectedV3Farm],
  );

  const quickButtonsCore = useMemo(
    () => generateQuickButtonsConfig(farmPriorityTag, false),
    [farmPriorityTag],
  );

  const quickButtonsInverse = useMemo(
    () => generateQuickButtonsConfig(farmPriorityTag, true),
    [farmPriorityTag],
  );

  const quickButtons = useMemo(
    () => (isInverseMode ? quickButtonsInverse : quickButtonsCore),
    [isInverseMode, quickButtonsCore, quickButtonsInverse],
  );

  const isCustomMode = useMemo(
    () => quickPresetType === null,
    [quickPresetType],
  );

  const options = useMemo(
    () => [
      {
        label: currentV3PoolInfo?.token0?.symbol,
        value: currentV3PoolInfo?.token0?.address,
      },
      {
        label: currentV3PoolInfo?.token1?.symbol,
        value: currentV3PoolInfo?.token1?.address,
      },
    ],
    [currentV3PoolInfo],
  );

  const isOutRange = useMemo(
    () => !!selectedV3Farm.farm.poolV3PositionId && !currentV3PoolInfo?.tickIn,
    [selectedV3Farm, currentV3PoolInfo],
  );

  const enabledFarmData = useMemo(
    () =>
      selectedV3Farm.farmInfo.enabled
        ? { origin, dexFactoryName: selectedV3Farm.farmInfo.dexFactory.name }
        : undefined,
    [selectedV3Farm],
  );

  const isEditable = useMemo(
    () => !vault.isPublished && isAddressesEqual(account, vault.creator),
    [account, vault],
  );

  const connectorRange = quickButtonsCore.find(
    (item) => item.rangeWidthType === quickPresetType,
  );

  const isPercentagesChanged = useMemo(() => {
    if (
      currentV3PoolInfo?.initialMinPercent &&
      currentV3PoolInfo.initialMaxPercent
    ) {
      if (isInverseMode) {
        const { min: inverseMin, max: inverseMax } = inversePercent(
          toBN(currentV3PoolInfo.initialMinPercent),
          toBN(currentV3PoolInfo.initialMaxPercent),
        );

        const finalPercentage =
          quickPresetType === ConnectorRangeWidthType.Full
            ? { min: percentage.min, max: percentage.max }
            : { min: inverseMin, max: inverseMax };

        return (
          !toBN(percentage.min).equals(finalPercentage.min) ||
          !toBN(percentage.max).equals(finalPercentage.max)
        );
      }
      return (
        !toBN(percentage.min).equals(currentV3PoolInfo.initialMinPercent) ||
        !toBN(percentage.max).equals(currentV3PoolInfo.initialMaxPercent)
      );
    }
    return false;
  }, [percentage, currentV3PoolInfo, isInverseMode, quickPresetType]);

  const isDisabledRebalnce = useMemo(
    () => !isEditable || (isEditable && !isPercentagesChanged),
    [isEditable, isPercentagesChanged],
  );

  const isDisabledSwitch = useMemo(
    () => selectedV3Farm.farm.isAilmEnabled || !isEditable,
    [isEditable, selectedV3Farm],
  );

  const debouncedMin = useDebounce(percentage.min, { delay: 1000 });
  const debouncedMax = useDebounce(percentage.max, { delay: 1000 });

  const { amount0, amount1 } = useWeightAmountsLiquidity({
    farm: selectedV3Farm,
    min: debouncedMin,
    max: debouncedMax,
  });

  const {
    token0,
    token1,
    amountOutStakingTokenToNative,
    initialMaxPercent,
    initialMinPercent,
  } = currentV3PoolInfo;

  const {
    amountOutStakingTokenToNativeAdjusted,
    isPending: isPendingPriceRanges,
  } = useSimulateUpdatePriceRanges({
    selectedV3Farm,
    maxPercent: debouncedMax,
    minPercent: debouncedMin,
    currentV3PoolInfo,
    isDisabled: !isEditable,
  });

  const priceImpactPercent = useMemo(() => {
    if (
      !amountOutStakingTokenToNativeAdjusted ||
      !amountOutStakingTokenToNative ||
      !isEditable
    )
      return toBN(0);
    return amountOutStakingTokenToNativeAdjusted.div(
      amountOutStakingTokenToNative.minus(1),
    );
  }, [
    amountOutStakingTokenToNative,
    amountOutStakingTokenToNativeAdjusted,
    isEditable,
  ]);

  const priceImpactLevel = useMemo(
    () => getPriceImpactLevel(priceImpactPercent),
    [priceImpactPercent],
  );

  const handleTab = useCallback(
    (_: SyntheticEvent, value: string | number) => {
      const updatedPercentage = inversePercent(percentage.min, percentage.max);

      const isInverse = isAddressesEqual(
        String(value),
        currentV3PoolInfo.token1.address,
      );

      if (!isInverse && isConnectorRangeWidthType(quickPresetType)) {
        if (connectorRange) {
          setPercentage({
            min: connectorRange.min,
            max: connectorRange.max,
          });
        }
      } else {
        const values =
          quickPresetType === ConnectorRangeWidthType.Full && connectorRange
            ? {
                min: connectorRange.min,
                max: connectorRange.max,
              }
            : {
                min: toBN(updatedPercentage.min),
                max: toBN(updatedPercentage.max),
              };
        setPercentage(values);
      }

      setTokenTab(String(value));
    },
    [
      connectorRange,
      percentage,
      currentV3PoolInfo,
      farmPriorityTag,
      quickPresetType,
    ],
  );

  const handleSetMinPercent = useCallback((value: Decimal) => {
    if (value.gt(0)) return;
    setPercentage((prev) => ({ max: prev.max, min: value }));
  }, []);

  const handleSetMaxPercent = useCallback((value: Decimal) => {
    if (value.lt(0)) return;
    setPercentage((prev) => ({ min: prev.min, max: value }));
  }, []);

  const handleSetFarm = useCallback(
    (newFarm: VaultFarmsType) => {
      const farm = vaultFarmsV3Info[newFarm.farm.farmId];

      const { rangeWidthType } = newFarm.farm;

      const rangeWidth = QUICK_CONNECTOR_TYPE.includes(rangeWidthType)
        ? (rangeWidthType as unknown as ConnectorRangeWidthType)
        : null;

      setPercentage({
        min: toBN(farm.initialMinPercent),
        max: toBN(farm.initialMaxPercent),
      });

      setQuickPresetType(rangeWidth);
      setTokenTab(farm.token0.address);
      setV3Farm(newFarm);
    },
    [vaultFarmsV3Info],
  );

  const onSetCustomMode = useCallback(() => {
    if (selectedV3Farm.farm.isAilmEnabled || !isEditable) return;
    setQuickPresetType(null);
  }, [selectedV3Farm, isEditable]);

  const onResetState = useCallback(() => {
    const initialMin = toBN(initialMinPercent);
    const initialMax = toBN(initialMaxPercent);

    let initialState = {
      min: initialMin,
      max: initialMax,
    };

    if (isInverseMode) {
      const inverseData = inversePercent(initialMin, initialMax);

      initialState = {
        min: inverseData.min,
        max: inverseData.max,
      };
    }

    handleSetMinPercent(initialState.min);
    handleSetMaxPercent(initialState.max);
  }, [initialMaxPercent, initialMinPercent, isInverseMode]);

  const handleUpdate = useCallback(async () => {
    await Promise.all([vaultActions.onUpdateVault()]);
  }, []);

  const { updateLmConfig } = useUpdateConfigLmAction({
    handleUpdate,
  });

  const { writeContractAsync, isPending: isPendingPriceRange } =
    useUpdatePriceRange({
      vaultId: vault.id,
      handleUpdate,
    });

  const updatePriceRange = useCallback(
    async (minPercent: Decimal, maxPercent: Decimal) => {
      try {
        let min = minPercent;
        let max = maxPercent;

        if (isInverseMode) {
          if (quickPresetType === ConnectorRangeWidthType.Full) {
            min = minPercent;
            max = maxPercent;
          } else {
            const result = inversePercent(min, max);
            min = toBN(result.min.toFixed());
            max = toBN(result.max.toFixed());
          }
        }

        await writeContractAsync({
          selectedV3Farm,
          maxPercent: max,
          minPercent: min,
          currentV3PoolInfo,
        });
      } catch (e) {
        console.error(e, 'onUpdatePriceRange');
      }
    },
    [isInverseMode, selectedV3Farm, currentV3PoolInfo],
  );

  const onUpdatePriceRange = useCallback(
    async (min: Decimal, max: Decimal) => {
      if (priceImpactLevel === PriceImpactLevel.High) {
        modalActions.show(MODAL_TYPES.PRICE_IMPACT_MODAL, {
          onConfirm: updatePriceRange,
        });
        return;
      }

      await updatePriceRange(min, max);
    },
    [priceImpactLevel],
  );

  const handleClickQuickButton = useCallback(
    (min: Decimal, max: Decimal, rangeType: ConnectorRangeWidthType | null) => {
      setQuickPresetType(rangeType);
      handleSetMinPercent(min);
      handleSetMaxPercent(max);
    },
    [],
  );

  const handleChangeStatus = useCallback(async () => {
    if (isDisabledSwitch) return;

    const updatedFarm = await updateLmConfig({
      vaultId: vault.id,
      farmId: selectedV3Farm.farm.farmId,
      isAlmEnabled: !selectedV3Farm.farm.isAlmEnabled,
      isAilmEnabled: false,
    });

    if (updatedFarm) {
      setV3Farm((prevState) => ({
        ...prevState,
        farm: {
          ...prevState.farm,
          isAlmEnabled: updatedFarm.isAlmEnabled,
        },
      }));
    }
  }, [isDisabledSwitch, selectedV3Farm, vault]);

  const handleChangeLmConfigMode = useCallback(
    async (_: SyntheticEvent, newValue: number | string) => {
      if (!isEditable) return;

      try {
        const isAlmEnabled = newValue !== RangeTab.Automatic;
        const isAilmEnabled = newValue === RangeTab.Automatic;

        const updatedFarm = await updateLmConfig({
          vaultId: vault.id,
          farmId: selectedV3Farm.farm.farmId,
          isAlmEnabled,
          isAilmEnabled,
        });

        if (updatedFarm) {
          setV3Farm((prevState) => ({
            ...prevState,
            farm: {
              ...prevState.farm,
              isAilmEnabled: updatedFarm.isAilmEnabled,
              isAlmEnabled: updatedFarm.isAlmEnabled,
            },
          }));
        }
      } catch (e) {
        console.log(e);
      }
    },
    [isEditable, selectedV3Farm, vault],
  );

  return (
    <div className={classes.box}>
      <div className={classes.content}>
        <div className={classes.farms}>
          {vaultV3Farms.map((farm) => (
            <FarmButton
              key={farm.farm.farmId}
              farm={farm}
              onCLick={() => handleSetFarm(farm)}
              selectedFarm={selectedV3Farm}
            />
          ))}
        </div>

        <div className={classes.rangeInfoBox}>
          <div className={classes.rangeInfoRow}>
            <div className={classes.farmDetailsBox}>
              <FarmDetails
                chainId={vault.id}
                enabledFarmData={enabledFarmData}
                stakingToken={selectedV3Farm.farmInfo.stakingToken}
              />
              {farmTags.map((farmTag) => (
                <FarmV3Tag farmTag={farmTag} key={farmTag} />
              ))}
            </div>

            <div className={classes.rangeInfo}>
              <EstimatedApy
                vaultFarmsV3Info={vaultFarmsV3Info}
                selectedV3Farm={selectedV3Farm}
                minPercent={percentage.min}
                maxPercent={percentage.max}
                vaultFarms={vaultFarms}
                vault={vault}
              />
              <Typography
                variant="body2"
                color={(color) =>
                  isOutRange ? color.accent.a4 : color.accent.a1
                }
              >
                {isOutRange ? 'Out range' : 'In range'}
              </Typography>
            </div>

            <div className={classes.statusBox}>
              <AutomaticRebalancing selectedV3Farm={selectedV3Farm} />
              <Switch
                checked={selectedV3Farm.farm.isAlmEnabled}
                onChange={handleChangeStatus}
                disabled={isDisabledSwitch}
              />
            </div>
          </div>

          <div className={classes.switchRow}>
            <Tabs
              activeTab={tokenTab}
              onChange={handleTab}
              options={options}
              fullWidth={isMobile}
              className={classes.innerTab}
              variant={isMobile ? 'fullWidth' : 'standard'}
            />
            <div />

            <TokenWeight
              classNameItem={classes.tokenWeight}
              className={classes.tokensWeight}
              token0Weight={currentV3PoolInfo?.token0Weight}
              token1Weight={currentV3PoolInfo?.token1Weight}
              symbol0={currentV3PoolInfo?.token0?.symbol}
              symbol1={currentV3PoolInfo?.token1?.symbol}
            />
          </div>
        </div>
        <Chart
          onSetMinPercent={handleSetMinPercent}
          onSetMaxPercent={handleSetMaxPercent}
          selectedV3Farm={selectedV3Farm}
          currentV3PoolInfo={currentV3PoolInfo}
          minPercent={percentage.min}
          maxPercent={percentage.max}
          isPercentagesChanged={isPercentagesChanged}
          isEditable={isEditable}
          isCustomMode={isCustomMode}
          onResetState={onResetState}
          isInverseMode={isInverseMode}
          tokens={tokens}
        />
        <div className={classes.counters}>
          <CounterBox
            title="Min"
            startTokenSymbol={tokens.token1.symbol}
            endTokenSymbol={tokens.token0.symbol}
          >
            <ManageLiquidityCounter
              suffix="%"
              disabled={!isEditable || !isCustomMode}
              variant={Variant.CircleDark}
              count={percentage.min}
              inputValue={percentage.min}
              minLimitValue={PERCENTAGE_LIMIT_RANGE.initialMin.min}
              maxLimitValue={PERCENTAGE_LIMIT_RANGE.initialMin.max}
              onChange={(val) => handleSetMinPercent(val)}
              onCount={(val) => handleSetMinPercent(val)}
            />
          </CounterBox>
          <CounterBox
            title="Max"
            startTokenSymbol={tokens.token1.symbol}
            endTokenSymbol={tokens.token0.symbol}
          >
            <ManageLiquidityCounter
              suffix="%"
              disabled={!isEditable || !isCustomMode}
              variant={Variant.CircleDark}
              count={percentage.max}
              inputValue={percentage.max}
              minLimitValue={PERCENTAGE_LIMIT_RANGE.initialMax.min}
              maxLimitValue={PERCENTAGE_LIMIT_RANGE.initialMax.max}
              onChange={(val) => handleSetMaxPercent(val)}
              onCount={(val) => handleSetMaxPercent(val)}
            />
          </CounterBox>
        </div>

        <SelectRange
          isEditable={isEditable}
          isCustomMode={isCustomMode}
          quickButtons={quickButtons}
          quickPresetType={quickPresetType}
          selectedV3Farm={selectedV3Farm}
          onSetCustomMode={onSetCustomMode}
          onClickQuickButton={handleClickQuickButton}
          onChangeLmConfigMode={handleChangeLmConfigMode}
        />
        <ProjectedLiquidityPosition
          token0={token0}
          token1={token1}
          amount0={amount0}
          amount1={amount1}
          isPending={isPendingPriceRanges}
          priceImpactLevel={priceImpactLevel}
          priceImpactPercent={priceImpactPercent}
          isPercentagesChanged={isPercentagesChanged}
        />
      </div>
      <div className={classes.btns}>
        <Button
          fullWidth
          variant={Variant.Secondary}
          onClick={onCancel}
          size={isMobile ? Size.Medium : Size.Large}
        >
          Cancel
        </Button>
        <SwitchChainButton
          chainId={vault.chainId}
          title="Switch chain"
          size={isMobile ? Size.Medium : Size.Large}
          variant={isOutRange ? Variant.Warning : Variant.Contained}
        >
          <Button
            fullWidth
            checkAuth
            disabled={isDisabledRebalnce}
            className={classes.btn}
            isLoading={isPendingPriceRange}
            size={isMobile ? Size.Medium : Size.Large}
            onClick={() => onUpdatePriceRange(percentage.min, percentage.max)}
          >
            Rebalance Now
          </Button>
        </SwitchChainButton>
      </div>
    </div>
  );
};

interface ManageLiquidityWrapperProps {
  vault: VaultDto;
  vaultInfo: VaultInfoType;
  defaultV3Farm: VaultFarmsType | null;
  onCancel: () => void;
  isPendingV3Data?: boolean;
}

const ManageLiquidityWrapper: FC<ManageLiquidityWrapperProps> = ({
  vault,
  vaultInfo,
  defaultV3Farm,
  isPendingV3Data,
  onCancel,
}) => {
  const { vaultV3Farms, vaultFarmsV3Info } = vaultInfo;

  const initialSelectedV3Farm = useMemo(
    () => defaultV3Farm || vaultV3Farms[0],
    [defaultV3Farm, vaultV3Farms],
  );

  const [selectedV3Farm, setV3Farm] = useState<VaultFarmsType>(
    initialSelectedV3Farm,
  );

  const currentV3PoolInfo = useMemo(
    () => vaultFarmsV3Info[selectedV3Farm.farm.farmId],
    [selectedV3Farm, vaultFarmsV3Info],
  );

  const farmPriorityTag = useMemo(
    () => getPriorityFarmTag(selectedV3Farm),
    [selectedV3Farm],
  );

  const initialMode = useMemo(
    () =>
      determineInitialMode(
        farmPriorityTag,
        currentV3PoolInfo?.initialMinPercent,
        currentV3PoolInfo?.initialMaxPercent,
      ),
    [farmPriorityTag, currentV3PoolInfo],
  );

  const isDataPending = useMemo(
    () =>
      !!(
        !currentV3PoolInfo ||
        !currentV3PoolInfo?.initialMinPercent ||
        !currentV3PoolInfo?.initialMaxPercent ||
        !selectedV3Farm ||
        isPendingV3Data ||
        !farmPriorityTag
      ),
    [currentV3PoolInfo, selectedV3Farm, isPendingV3Data, farmPriorityTag],
  );

  return (
    <>
      {isDataPending ? (
        <Loader isCenter size={32} />
      ) : (
        <ManageLiquidity
          setV3Farm={setV3Farm}
          vault={vault}
          vaultInfo={vaultInfo}
          onCancel={onCancel}
          initialMode={initialMode}
          currentV3PoolInfo={currentV3PoolInfo}
          selectedV3Farm={selectedV3Farm}
          farmPriorityTag={farmPriorityTag}
        />
      )}
    </>
  );
};

export default memo(ManageLiquidityWrapper);
